import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserCheck, BarChart3, Activity, Upload,
  UsersRound, ShieldCheck, Radio, LogOut, Bell, Moon, Sun, Menu, X, KeyRound, Mail,
  Globe2, ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useTimezone, TIMEZONES } from "../context/TimezoneContext";
import api from "../lib/api";
import { Avatar, Modal, toast } from "./ui";
import ReminderToasts from "./ReminderToasts";
import { ROLE_META } from "../lib/constants";
import { formatDistanceToNow } from "date-fns";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/clients", label: "Clients", icon: UserCheck },
  { to: "/analytics", label: "Analytics", icon: BarChart3, admin: true },
  { to: "/activity-logs", label: "Activity Logs", icon: Activity },
  { to: "/upload", label: "Upload Data", icon: Upload, upload: true },
  { to: "/agents", label: "Manage Agents", icon: UsersRound, admin: true },
  { to: "/admin", label: "Admin Panel", icon: ShieldCheck, admin: true },
  { to: "/relay", label: "Lead Relay", icon: Radio, admin: true },
  // Mail keeps its own session, but arriving from here is an explicit "take me
  // to my mail": clear the flag that a previous sign-out set, so the page signs
  // this CRM account straight in instead of asking for a password it has not got.
  { to: "/email", label: "Email", icon: Mail,
    onNav: () => { try { localStorage.removeItem("mail-no-auto"); } catch { /* private mode */ } } },
];

function Clock() {
  const [now, setNow] = useState(new Date());
  const { tz, setTz, label } = useTimezone();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  let date = "", time = "";
  try {
    date = new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", day: "2-digit", month: "short" }).format(now);
    time = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(now);
  } catch {
    date = now.toDateString();
    time = now.toLocaleTimeString();
  }
  return (
    <div className="hidden sm:flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 pl-3 pr-1.5 py-1" data-testid="topbar-clock" ref={ref}>
      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{date}</span>
      <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{time}</span>
      <div className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="tz-badge"
          className="flex items-center gap-1 rounded-md bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors"
        >
          <Globe2 className="w-3 h-3" /> {label} <ChevronDown className="w-3 h-3" />
        </button>
        {open && (
          <div className="absolute right-0 mt-2 w-48 max-h-72 overflow-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#111827] shadow-xl animate-zoom-in z-50 py-1" data-testid="tz-menu">
            {TIMEZONES.map((t) => {
              let local = "";
              try { local = new Intl.DateTimeFormat("en-GB", { timeZone: t.tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(now); } catch {}
              return (
                <button
                  key={t.tz}
                  onClick={() => { setTz(t.tz); setOpen(false); }}
                  data-testid={`tz-opt-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${t.tz === tz ? "text-indigo-600 dark:text-indigo-400 font-semibold" : "text-slate-700 dark:text-slate-200"}`}
                >
                  <span>{t.label}</span>
                  <span className="font-mono text-xs text-slate-400">{local}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Notifications() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);

  const load = async () => {
    try { const r = await api.get("/notifications"); setData(r.data); } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <div className="relative" ref={ref}>
      <button className="btn-ghost p-2 relative" onClick={() => setOpen((o) => !o)} data-testid="notif-bell" aria-label="Notifications">
        <Bell className="w-5 h-5" />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{data.unread > 99 ? "99+" : data.unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[28rem] overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#111827] shadow-xl animate-zoom-in z-50" data-testid="notif-panel">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4 py-3">
            <span className="font-semibold text-slate-900 dark:text-white">Notifications</span>
            <button className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline" onClick={markAll} data-testid="notif-mark-all">Mark all read</button>
          </div>
          {data.items.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications</div>}
          {data.items.map((n) => (
            <div key={n.id} className={`border-b border-slate-50 dark:border-slate-800/50 px-4 py-3 ${n.is_read ? "" : "bg-indigo-50/40 dark:bg-indigo-500/5"}`}>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{n.title}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{n.message}</div>
              <div className="text-[11px] text-slate-400 mt-1">{n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChangePasswordModal({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast("Password updated");
      onClose(); setCur(""); setNw("");
    } catch (e) {
      toast(e.response?.data?.detail || "Failed to update password", "error");
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Change Password" testid="change-pw-modal"
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy || !cur || !nw} data-testid="change-pw-submit">Update</button>
      </>}>
      <div>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Current password</label>
        <input type="password" className="input mt-1" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-pw-input" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">New password</label>
        <input type="password" className="input mt-1" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-pw-input" />
      </div>
    </Modal>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const isAdmin = !!user?.is_admin;
  const items = NAV.filter((n) => (!n.admin || isAdmin) && (!n.upload || isAdmin || !!user?.can_upload));

  const doLogout = async () => { await logout(); nav("/login"); };

  const SidebarContent = (
    <>
      <div className="px-5 py-5 border-b border-slate-200 dark:border-slate-800">
        <div className="text-xl font-extrabold font-display tracking-tight text-slate-900 dark:text-white">
          Sales<span className="text-indigo-600 dark:text-indigo-400">CRM</span>
        </div>
        <div className="text-xs text-slate-400 mt-0.5">{isAdmin ? "Admin Dashboard" : "Agent Workspace"}</div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={() => { n.onNav?.(); setMobileOpen(false); }}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
                  : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-200"
              }`
            }
          >
            <n.icon className="w-5 h-5" strokeWidth={1.8} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 dark:border-slate-800 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar name={user?.full_name} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{user?.full_name}</div>
            <div className="truncate text-xs text-slate-400">@{user?.username}</div>
          </div>
        </div>
        <button className="btn-ghost w-full justify-start mt-1" onClick={() => { setPwOpen(true); setMobileOpen(false); }} data-testid="change-password-btn">
          <KeyRound className="w-4 h-4" /> Change Password
        </button>
        <button className="btn-ghost w-full justify-start text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10" onClick={doLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B0F19]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-shrink-0 flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111827]">
        {SidebarContent}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-10 flex w-64 flex-col bg-white dark:bg-[#111827] animate-slide-up">{SidebarContent}</aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="h-16 flex items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-[#111827]/80 backdrop-blur-md px-4 sm:px-6 z-40">
          <div className="flex items-center gap-3">
            <button className="btn-ghost p-2 md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
            <Clock />
          </div>
          <div className="flex items-center gap-2">
            {user?.role && (
              <span className={`badge hidden sm:inline-flex ${ROLE_META[user.role]?.cls || ROLE_META.agent.cls}`}>{ROLE_META[user.role]?.label || user.role}</span>
            )}
            <button className="btn-ghost p-2" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Notifications />
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8 animate-fade-in">{children}</main>
      </div>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      <ReminderToasts />
    </div>
  );
}
